import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { etiquetaNivel } from '@/lib/nivelesUsuario';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, UserCog, Shield, Pencil, Trash2, ShieldAlert, MessageSquare, Mail, Link as LinkIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PhoneInput from '@/components/PhoneInput';
import { LimitUpgradeDialog } from '@/components/LimitUpgradeDialog';
import CopyableId from '@/components/CopyableId';
import UsageBadge from '@/components/UsageBadge';
import useUsageSummary from '@/hooks/useUsageSummary';

const PERMISSION_LABELS = {
  properties: 'Propiedades', leads: 'Leads', team: 'Equipo', settings: 'Config',
  opportunities: 'Oportunidades', rules: 'Reglas', ai_agents: 'IA', integrations: 'Integraciones'
};

// 'write' se pintaba como "Lectura", que es falso. Cada acceso con su palabra.
const ETIQUETA_ACCESO = { both: 'Total', read: 'Lectura', write: 'Escritura', none: 'Sin acceso' };
const PERM_OPTIONS = ['none', 'read', 'both'];
const permColor = (v) => v === 'both' ? 'bg-[#A5D62C]/10 text-[#A5D62C] border-[#A5D62C]/20' : v === 'read' ? 'bg-blue-400/10 text-blue-400 border-blue-400/20' : 'bg-muted/30 text-muted-foreground border-white/5';

export default function TeamPage() {
  const { user } = useAuth();
  const industry = user?.industry || user?.company?.industry || 'real_estate';
  const { data: usage, reload: reloadUsage } = useUsageSummary();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', whatsapp_number: '', role: 'vendor', permissions: {} });
  const [userLimitDialogOpen, setUserLimitDialogOpen] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const usersLimit = Number(user?.license_profile?.limits?.users ?? -1);
  const isUsersLimitReached = Number.isFinite(usersLimit) && usersLimit >= 0 && members.length >= usersLimit;

  const load = useCallback(() => {
    api.get('/team').then(r => { setMembers(Array.isArray(r.data) ? r.data : []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    if (isUsersLimitReached) {
      setUserLimitDialogOpen(true);
      return;
    }
    setEditing(null);
    setForm({ name: '', email: '', password: '', phone: '', whatsapp_number: '', role: 'vendor', receives_leads: true, let_user_set_password: true, permissions: { properties: 'read', leads: 'read', team: 'none', settings: 'none', ai_agents: 'none', integrations: 'none', opportunities: 'read', rules: 'none' } });
    setDialogOpen(true);
  };

  const openEdit = (member) => {
    setEditing(member.id);
    setForm({
      name: member.name || '', email: member.email || '', password: '', phone: member.phone || '',
      whatsapp_number: member.whatsapp_number || '',
      role: member.role || 'vendor',
      receives_leads: typeof member.receives_leads === 'boolean' ? member.receives_leads : (member.role === 'vendor'),
      let_user_set_password: false,
      permissions: member.permissions || {}
    });
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, phone: form.phone, whatsapp_number: form.whatsapp_number, role: form.role, receives_leads: !!form.receives_leads, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await api.put(`/team/${editing}`, payload);
        toast.success('Miembro actualizado');
      } else {
        if (!form.let_user_set_password && !form.password) {
          toast.error('Ingresa una contrasena o marca la opcion "El usuario creara su propia contrasena"');
          return;
        }
        const payload = {
          name: form.name,
          email: form.email,
          phone: form.phone,
          whatsapp_number: form.whatsapp_number,
          permissions: { ...form.permissions, _role: form.role, _receives_leads: form.receives_leads ? 'true' : 'false' },
          password: form.let_user_set_password ? '' : form.password,
        };
        await api.post('/team', payload);
        toast.success(form.let_user_set_password
          ? 'Miembro creado. Se envio link de activacion por email.'
          : 'Miembro creado');
      }
      setDialogOpen(false);
      load();
      reloadUsage();
    } catch (e) {
      const rawDetail = e.response?.data?.detail;
      const detail = Array.isArray(rawDetail)
        ? rawDetail.map(d => d.msg || JSON.stringify(d)).join(', ')
        : (typeof rawDetail === 'string' ? rawDetail : 'Error al crear usuario');
      if (e.response?.status === 403 && detail.toLowerCase().includes('limite')) {
        setUserLimitDialogOpen(true);
      }
      toast.error(detail);
    }
  };

  const remove = async (member) => {
    const grupos = Array.isArray(member.roulette_groups) ? member.roulette_groups : [];
    let msg = 'Eliminar miembro?';
    if (grupos.length === 1) {
      msg = `Este usuario pertenece al grupo de asignación "${grupos[0]}". Se eliminará también de ese grupo. ¿Continuar?`;
    } else if (grupos.length > 1) {
      const nombres = grupos.map(g => `"${g}"`).join(', ');
      msg = `Este usuario pertenece a los grupos de asignación ${nombres}. Se eliminará también de esos grupos. ¿Continuar?`;
    }
    if (!window.confirm(msg)) return;
    await api.delete(`/team/${member.id}`);
    toast.success('Eliminado');
    load();
  };

  const resendInvitation = async (id) => {
    try {
      await api.post(`/team/${id}/resend-invitation`);
      toast.success('Email reenviado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reenviar');
    }
  };

  const copyActivationLink = async (id) => {
    try {
      const r = await api.post(`/team/${id}/activation-link`);
      await navigator.clipboard.writeText(r.data.activation_link);
      toast.success('Link copiado al portapapeles (valido 48h)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar link');
    }
  };

  const togglePerm = (key) => {
    const perms = form.permissions || {};
    const current = perms[key] || 'none';
    const next = PERM_OPTIONS[(PERM_OPTIONS.indexOf(current) + 1) % PERM_OPTIONS.length];
    setForm({ ...form, permissions: { ...perms, [key]: next } });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="team-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white">Equipo</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-muted-foreground text-sm">{members.length} miembros</p>
            <UsageBadge status={usage?.users} label="Usuarios" testId="team-usage-badge" />
          </div>
          {isUsersLimitReached && <p className="text-[11px] text-red-300 mt-1" data-testid="team-limit-warning">Límite de usuarios alcanzado.</p>}
        </div>
        {isAdmin && (
          <Button onClick={openNew} className={`font-semibold ${isUsersLimitReached ? 'bg-red-600/20 border border-red-500/50 text-red-200 hover:bg-red-600/30' : 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20'}`} data-testid="add-member-btn">
            <Plus className="w-4 h-4 mr-2" />Agregar Miembro
          </Button>
        )}
      </div>
      {members.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5"><CardContent className="py-16 text-center"><UserCog className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" /><p className="text-muted-foreground">Sin miembros de equipo</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {members.map(member => (
            <Card key={member.id} className="bg-card/50 backdrop-blur-md border-white/5 hover:border-white/10" style={{ transition: 'border-color 0.2s ease' }} data-testid={`member-card-${member.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${member.role === 'admin' ? 'bg-[#A5D62C]/20 text-[#A5D62C]' : 'bg-primary/10 text-primary'}`}>
                      {member.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">{member.name}</p>
                      <CopyableId id={member.id} />
                      <p className="text-[11px] text-muted-foreground">{member.email}</p>
                      {member.phone && <p className="text-[11px] text-muted-foreground mt-0.5">{member.phone}</p>}
                      {member.whatsapp_number && <p className="text-[10px] text-emerald-400/70 mt-0.5 flex items-center gap-1"><MessageSquare className="w-2.5 h-2.5" />{member.whatsapp_number}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    <Badge variant="outline" className={`text-[9px] ${member.role === 'admin' ? 'border-[#A5D62C]/30 text-[#A5D62C]' : member.role === 'manager' ? 'border-cyan-400/30 text-cyan-400' : member.role === 'guard' ? 'border-amber-400/30 text-amber-400' : 'border-white/10 text-muted-foreground'}`}>
                      {member.role === 'guard' ? <ShieldAlert className="w-2.5 h-2.5 mr-1" /> : <Shield className="w-2.5 h-2.5 mr-1" />}
                      {etiquetaNivel(member.role)}
                    </Badge>
                    {member.is_active === false && (
                      <Badge variant="outline" className="text-[9px] border-amber-400/40 text-amber-300 bg-amber-400/5" data-testid={`member-pending-badge-${member.id}`}>
                        Pendiente
                      </Badge>
                    )}
                    {member.receives_leads === true && (
                      <Badge
                        variant="outline"
                        className="text-[9px] border-emerald-400/40 text-emerald-300 bg-emerald-400/5"
                        data-testid={`member-receives-leads-badge-${member.id}`}
                      >
                        Recibe leads
                      </Badge>
                    )}
                    {isAdmin && (
                      <>
                        {member.is_active === false && member.id !== user.id && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => resendInvitation(member.id)} className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10" title="Reenviar invitacion por email" data-testid={`resend-invitation-${member.id}`}><Mail className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => copyActivationLink(member.id)} className="h-7 w-7 p-0 text-muted-foreground hover:text-cyan-400 hover:bg-cyan-500/10" title="Copiar link de activacion" data-testid={`copy-activation-link-${member.id}`}><LinkIcon className="w-3.5 h-3.5" /></Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEdit(member)} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#A5D62C] hover:bg-[#A5D62C]/10" data-testid={`edit-member-${member.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        {member.id !== user.id && (
                          <Button variant="ghost" size="sm" onClick={() => remove(member)} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10" data-testid={`delete-member-${member.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {/* Las etiquetas muestran el acceso EFECTIVO (plan Y tipo ya resueltos), que es
                    lo mismo que decide el menú. Antes se pintaban desde el campo HEREDADO, así
                    que alguien podía llevar el cartel "IA: Total" y no ver Agente IA: un cartel
                    que miente. Si el servidor no manda `acceso_efectivo` (versión vieja o fallo
                    al resolver), se cae a lo de antes: "no sé" no puede dejar la ficha en blanco. */}
                {member.acceso_efectivo ? (
                  <div className="flex flex-wrap gap-1" data-testid={`acceso-efectivo-${member.id}`}>
                    {Object.entries(member.acceso_efectivo).map(([k, info]) => (
                      <span key={k} className={`px-1.5 py-0.5 rounded text-[9px] font-medium border ${permColor(info.acceso)}`}>
                        {info.etiqueta}: {ETIQUETA_ACCESO[info.acceso] || info.acceso}
                      </span>
                    ))}
                  </div>
                ) : member.permissions ? (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(member.permissions).filter(([, v]) => v !== 'none').map(([k, v]) => (
                      <span key={k} className={`px-1.5 py-0.5 rounded text-[9px] font-medium border ${permColor(v)}`}>
                        {PERMISSION_LABELS[k] || k}: {ETIQUETA_ACCESO[v] || v}
                      </span>
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#1F2833] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{editing ? 'Editar Miembro' : 'Nuevo Miembro'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{editing ? 'Actualiza datos y permisos' : 'Agrega un nuevo miembro al equipo'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input data-testid="member-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Email</Label><Input data-testid="member-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary" /></div>
              <div className="space-y-2">
                <Label>{editing ? 'Nueva Contrasena (dejar vacio para no cambiar)' : 'Contrasena'}</Label>
                <Input
                  data-testid="member-password"
                  type="password"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  disabled={!editing && form.let_user_set_password}
                  placeholder={!editing && form.let_user_set_password ? 'El usuario la creara' : ''}
                  className="bg-black/20 border-white/10 focus:border-primary disabled:opacity-40"
                />
              </div>
            </div>
            {!editing && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-1.5" data-testid="let-user-set-password-block">
                <label className="flex items-center gap-2 cursor-pointer text-xs text-white">
                  <input
                    type="checkbox"
                    checked={!!form.let_user_set_password}
                    onChange={e => setForm({ ...form, let_user_set_password: e.target.checked, password: e.target.checked ? '' : form.password })}
                    data-testid="let-user-set-password-checkbox"
                  />
                  <span className="font-medium">El usuario creara su propia contrasena (recomendado)</span>
                </label>
                <p className="text-[10px] text-muted-foreground leading-relaxed">Se enviara un link al correo del usuario para que defina su contrasena. El link expira en 48 horas.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Telefono</Label><PhoneInput data-testid="member-phone" value={form.phone} onChange={v => setForm({...form, phone: v})} /></div>
              <div className="space-y-2">
                <Label>WhatsApp</Label>
                <PhoneInput data-testid="member-whatsapp" value={form.whatsapp_number} onChange={v => setForm({...form, whatsapp_number: v})} placeholder="Numero de WhatsApp" />
                <p className="text-[10px] text-muted-foreground">Se usa para asignacion de leads via WhatsApp</p>
              </div>
            </div>
            <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={v => {
                    const newPerms = v === 'guard'
                    ? { properties: 'read', leads: 'none', team: 'none', settings: 'none', ai_agents: 'none', integrations: 'none', opportunities: 'none', rules: 'none' }
                      : v === 'manager'
                        ? { properties: 'both', leads: 'both', team: 'read', settings: 'none', ai_agents: 'read', integrations: 'none', opportunities: 'both', rules: 'read' }
                        : v === 'admin'
                          ? { properties: 'both', leads: 'both', team: 'both', settings: 'both', ai_agents: 'both', integrations: 'both', opportunities: 'both', rules: 'both' }
                    : { properties: 'read', leads: 'read', team: 'none', settings: 'none', ai_agents: 'none', integrations: 'none', opportunities: 'read', rules: 'none' };
                  setForm({ ...form, role: v, permissions: { ...form.permissions, ...newPerms } });
                }}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="member-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vendor">Promotor / Vendedor</SelectItem>
                    <SelectItem value="guard">Guardia</SelectItem>
                    {user?.role !== 'manager' && <SelectItem value="manager">Gerente</SelectItem>}
                    {user?.role === 'admin' || user?.role === 'superadmin' ? <SelectItem value="admin">Admin</SelectItem> : null}
                  </SelectContent>
                </Select>
              </div>
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid="member-receives-leads-row">
              <div>
                <Label className="text-sm">Recibe leads</Label>
                <p className="text-[10px] text-muted-foreground">
                  Si está activo, este miembro entra al pool de asignación automática.
                </p>
              </div>
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#A5D62C]"
                checked={!!form.receives_leads}
                onChange={(e) => setForm({ ...form, receives_leads: e.target.checked })}
                data-testid="member-receives-leads-toggle"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-primary" />Permisos (click para cambiar nivel)</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(PERMISSION_LABELS).filter(([key]) => {
                  if (key === 'properties' && industry !== 'real_estate') return false;
                  return true;
                }).map(([key, label]) => {
                  const val = form.permissions?.[key] || 'none';
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => togglePerm(key)}
                      className={`p-2.5 rounded-lg border text-left transition-colors ${permColor(val)}`}
                      data-testid={`perm-${key}`}
                    >
                      <p className="text-xs font-medium">{label}</p>
                      <p className="text-[10px] opacity-70">{val === 'both' ? 'Leer + Escribir' : val === 'read' ? 'Solo lectura' : 'Sin acceso'}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <Button onClick={save} className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold" data-testid="save-member-btn">{editing ? 'Actualizar' : 'Crear'} Miembro</Button>
          </div>
        </DialogContent>
      </Dialog>

      <LimitUpgradeDialog
        open={userLimitDialogOpen}
        onOpenChange={setUserLimitDialogOpen}
        title="Límite de usuarios alcanzado"
        description="Tu plan ya alcanzó el máximo de usuarios del equipo."
        current={members.length}
        limit={usersLimit < 0 ? 0 : usersLimit}
        testIdPrefix="users-limit"
      />
    </div>
  );
}
